import React, { useState, useEffect, useRef } from "react";
import { InspectionsClient } from "../web-api-client.ts";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { Button, InputGroup, Form } from "react-bootstrap";
import BackButton from "./BackButton.js";
import { isMobileDevice } from "./utils/utils.js";
import ClearableInput from "./ClearableInput.js";
import ScannerLauncher from "./ScannerLauncher.js";
import Loader from "./Loader.js";
import UserContext from "../providers/UserContext.js";

const Inspections = ({ status: initialFilterStatus }) => {
  const { barcode } = useParams(); // (currently unused, leaving as-is)
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState(initialFilterStatus || false); // (currently unused, leaving as-is)

  const [sortColumn, setSortColumn] = useState("created");
  const [sortDirection, setSortDirection] = useState("desc");

  const [filtersReady, setFiltersReady] = useState(false);
  const [filters, setFilters] = useState({ status: null });

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const hasLoadedRef = useRef(false);
  const reqIdRef = useRef(0);

  const user = React.useContext(UserContext);

  const itemsPerPage = 15;

  // Utility to build query string
  const buildQuery = () => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    if (filters.status !== null && filters.status !== undefined) {
      params.set("status", String(filters.status));
    }
    params.set("sort", sortColumn);
    params.set("dir", sortDirection);
    params.set("page", String(currentPage));
    return `?${params.toString()}`;
  };

  const handleRowClick = (inspectionId) => {
    navigate(`/dvi/${inspectionId}`);
  };

  const populateInspectionData = async (page, state) => {
    // Prevent stale requests from overwriting newer ones
    const reqId = ++reqIdRef.current;

    setLoading(true);
    const client = new InspectionsClient();

    const {
      searchTerm: s,
      sortColumn: sc,
      sortDirection: sd,
      filters: f,
    } =
      state ?? {
        searchTerm,
        sortColumn,
        sortDirection,
        filters,
      };

    try {
      const data = await client.getInspectionList(
        user.dealerId,
        page,
        itemsPerPage,
        s,
        sc,
        sd,
        f?.status ?? null
      );

      // Ignore stale results
      if (reqId !== reqIdRef.current) return;

      setInspections(data.items);
      setTotalPages(data.totalPages);
    } finally {
      // Only clear loading if this is still the latest request
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  // Initial read from querystring on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      const initialSearch = searchParams.get("search") || "";
      const statusStr = searchParams.get("status");
      const initialStatus =
        statusStr !== null && !Number.isNaN(Number(statusStr))
          ? Number(statusStr)
          : null;
      const initialSort = searchParams.get("sort") || "created";
      const initialDir = searchParams.get("dir") || "desc";
      const initialPage = parseInt(searchParams.get("page"), 10) || 1;

      setSearchTerm(initialSearch);
      setFilters({ status: initialStatus });
      setSortColumn(initialSort);
      setSortDirection(initialDir);
      setCurrentPage(initialPage);

      setFiltersReady(true);
    };

    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once we have initial filters, load data exactly once, then allow URL syncing & param watching
  useEffect(() => {
    if (!filtersReady) return;

    populateInspectionData(currentPage, {
      searchTerm,
      sortColumn,
      sortDirection,
      filters,
    });

    hasLoadedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersReady]);

  // Watch URL param changes (back/forward nav, manual edits)
  useEffect(() => {
    if (!hasLoadedRef.current) return;

    const newSearch = searchParams.get("search") || "";
    const newSort = searchParams.get("sort") || "created";
    const newDir = searchParams.get("dir") || "desc";
    const statusRaw = searchParams.get("status");
    const newStatus = statusRaw !== null ? Number(statusRaw) : null;
    const newPage = parseInt(searchParams.get("page"), 10) || 1;

    setSearchTerm(newSearch);
    setFilters({ status: newStatus });
    setSortColumn(newSort);
    setSortDirection(newDir);
    setCurrentPage(newPage);
  }, [searchParams]);

  // Single “source of truth” fetch effect (no other direct fetching on searchTerm change)
  useEffect(() => {
    if (!hasLoadedRef.current) return;

    populateInspectionData(currentPage, {
      searchTerm,
      sortColumn,
      sortDirection,
      filters,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filters, sortColumn, sortDirection, currentPage]);

  // Keep URL in sync with state (but avoid doing this before initial hydration)
  useEffect(() => {
    if (!hasLoadedRef.current) return;
    navigate(buildQuery(), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filters, sortColumn, sortDirection, currentPage]);

  const resetList = async () => {
    // Don’t fetch directly; just reset state and let the single effect fetch
    setSearchTerm("");
    setFilters({ status: null });
    setSortColumn("created");
    setSortDirection("desc");
    setCurrentPage(1);
  };

  const handleAddInspection = () => {
    navigate("/dvi/new");
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleScan = (scannedBarcode) => {
    setSearchTerm(scannedBarcode);
    setCurrentPage(1);
  };

  const handleSearch = async (eOrString) => {
    // Enter/search triggers should not fetch directly; they should just ensure state is set
    const value =
      typeof eOrString === "string"
        ? eOrString
        : eOrString?.target?.value ?? searchTerm;

    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const handleStatusChange = (e) => {
    const v = e.target.value;
    setFilters({ status: v === "" ? null : Number(v) });
    setCurrentPage(1);
  };

  const clearStatus = () => {
    setFilters({ status: null });
    setCurrentPage(1);
  };

  const clearAll = () => {
    setSearchTerm("");
    setFilters({ status: null });
    setSortColumn("created");
    setSortDirection("desc");
    setCurrentPage(1);
  };

  return (
    <div className="container mb-5" style={{ marginBottom: "90px!important" }}>
      <BackButton />
      <h1>Inspections</h1>

      <div className="row g-2 align-items-center">
        <div className="col-12 col-md-8 d-flex flex-wrap align-items-center gap-3">
          <InputGroup style={{ maxWidth: "300px" }}>
            <ClearableInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onEnter={handleSearch}
              onClear={resetList}
            />
            <ScannerLauncher
              onBarcodeDetected={handleScan}
              mode="button"
              buttonClasses="ms-2"
            />
          </InputGroup>

          {/* Status filter */}
          <Form.Select
            size="sm"
            style={{ width: 200 }}
            value={
              filters.status === null || filters.status === undefined
                ? ""
                : String(filters.status)
            }
            onChange={handleStatusChange}
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            <option value="0">In-Progress</option>
            <option value="1">Completed</option>
          </Form.Select>

          {/* Clear status (only shows when a status is applied) */}
          {filters.status !== null && filters.status !== undefined && (
            <Button variant="outline-secondary" size="sm" onClick={clearStatus}>
              Clear status
            </Button>
          )}

          {/* Optional: clear everything */}
          <Button variant="outline-dark" size="sm" onClick={clearAll}>
            Clear all
          </Button>
        </div>

        <div className="col-12 col-md-4 d-flex justify-content-md-end">
          <button
            className="btn btn-primary w-100 w-md-auto"
            onClick={handleAddInspection}
          >
            Add Inspection
          </button>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <table className="table table-hover align-middle">
          <thead>
            <tr>
              <th
                onClick={() => handleSort("vehicleStockNumber")}
                style={{ cursor: "pointer" }}
              >
                Stock Number
                {sortColumn === "vehicleStockNumber" &&
                  (sortDirection === "asc" ? "▲" : "▼")}
              </th>
              <th
                onClick={() => handleSort("vehicleYear")}
                style={{ cursor: "pointer" }}
              >
                Year{" "}
                {sortColumn === "vehicleYear" &&
                  (sortDirection === "asc" ? "▲" : "▼")}
              </th>
              <th
                onClick={() => handleSort("vehicleMake")}
                style={{ cursor: "pointer" }}
              >
                Make{" "}
                {sortColumn === "vehicleMake" &&
                  (sortDirection === "asc" ? "▲" : "▼")}
              </th>
              <th
                onClick={() => handleSort("vehicleModel")}
                style={{ cursor: "pointer" }}
              >
                Model{" "}
                {sortColumn === "vehicleModel" &&
                  (sortDirection === "asc" ? "▲" : "▼")}
              </th>
              <th
                onClick={() => handleSort("status")}
                style={{ cursor: "pointer" }}
              >
                Status{" "}
                {sortColumn === "status" &&
                  (sortDirection === "asc" ? "▲" : "▼")}
              </th>

              {!isMobileDevice() && (
                <th
                  onClick={() => handleSort("inspectionDate")}
                  style={{ cursor: "pointer" }}
                >
                  Inspection Date{" "}
                  {sortColumn === "inspectionDate" &&
                    (sortDirection === "asc" ? "▲" : "▼")}
                </th>
              )}

              <th
                onClick={() => handleSort("viewCount")}
                style={{ cursor: "pointer" }}
              >
                View Count{" "}
                {sortColumn === "viewCount" &&
                  (sortDirection === "asc" ? "▲" : "▼")}
              </th>
               <th
                onClick={() => handleSort("scanCount")}
                style={{ cursor: "pointer" }}
              >
                Scan Count{" "}
                {sortColumn === "scanCount" &&
                  (sortDirection === "asc" ? "▲" : "▼")}
              </th>
            </tr>
          </thead>

          <tbody>
            {inspections.map((inspection) => (
              <tr
                key={inspection.id}
                onClick={() => handleRowClick(inspection.id)}
                style={{ cursor: "pointer" }}
              >
                <td>{inspection.vehicleStockNumber}</td>
                <td>{inspection.vehicleYear}</td>
                <td>{inspection.vehicleMake}</td>
                <td>{inspection.vehicleModel}</td>
                <td>{inspection.status ? "Completed" : "In-Progress"}</td>

                {!isMobileDevice() && (
                  <td>
                    {new Date(inspection.inspectionDate).toLocaleString(
                      undefined,
                      {
                        dateStyle: "medium",
                        timeStyle: "short",
                        hour12: true,
                      }
                    )}
                  </td>
                )}

                <td>{inspection.viewCount || 0}</td>
                <td>{inspection.scanCount || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Pagination */}
      <nav className="mb-6">
        <ul className="pagination justify-content-end">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage - 1)}
            >
              Previous
            </button>
          </li>

          {Array.from({ length: totalPages }, (_, index) => (
            <li
              key={index}
              className={`page-item ${currentPage === index + 1 ? "active" : ""}`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </button>
            </li>
          ))}

          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default Inspections;
